/**
 * AgentsPanel — Harness health, scheduled jobs, and dispatch runtime.
 *
 * APIs wired:
 *   GET /harness/doctor → DoctorResponse (healthy, summary, checks)
 *   GET /scheduler/jobs → NOT wired (endpoint exists in seitiate but not in router)
 *   GET /standing-orders → NOT wired (model exists but no REST endpoint)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { SettingsSection } from '@seitiate/settings-ui';
import { API_V1 } from '@/lib/env';
import { buildEngineHeaders } from '@/lib/auth';

interface DoctorCheck {
    name: string;
    status: string;
    detail: string;
}

interface DoctorResponse {
    healthy: boolean;
    summary: string;
    checks: DoctorCheck[];
}

const CHECK_STATUS_STYLE: Record<string, { Icon: typeof CheckCircle2; color: string }> = {
    ok: { Icon: CheckCircle2, color: 'var(--theme-success)' },
    warn: { Icon: AlertTriangle, color: 'var(--theme-warning)' },
    fail: { Icon: XCircle, color: 'var(--theme-danger)' },
};

export const AgentsPanel: React.FC = () => {
    const [doctor, setDoctor] = useState<DoctorResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchDoctor = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await fetch(`${API_V1}/harness/doctor`, { headers: buildEngineHeaders() });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            setDoctor(await resp.json());
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load harness status');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchDoctor();
    }, [fetchDoctor]);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3
                    className="text-xs uppercase tracking-[0.2em]"
                    style={{ color: 'var(--theme-text-secondary)', fontFamily: 'var(--theme-font-mono)' }}
                >
                    Agents &amp; Harness
                </h3>
                <span
                    className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                    style={{
                        color: 'var(--theme-accent-primary)',
                        background: 'color-mix(in srgb, var(--theme-accent-primary) 10%, transparent)',
                        fontFamily: 'var(--theme-font-mono)',
                        borderRadius: 'var(--theme-radius-sm)',
                    }}
                >
                    Read-only
                </span>
            </div>

            {/* Harness Doctor */}
            <SettingsSection title="Harness Doctor">
                {loading ? (
                    <div
                        className="flex items-center gap-2 py-4 text-xs"
                        style={{ color: 'var(--theme-text-muted)', fontFamily: 'var(--theme-font-mono)' }}
                    >
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Checking harness health…
                    </div>
                ) : error ? (
                    <div
                        className="text-xs rounded px-3 py-2"
                        style={{
                            color: 'var(--theme-danger)',
                            background: 'color-mix(in srgb, var(--theme-danger) 8%, transparent)',
                            border: '1px solid color-mix(in srgb, var(--theme-danger) 20%, transparent)',
                            fontFamily: 'var(--theme-font-mono)',
                            borderRadius: 'var(--theme-radius-sm)',
                        }}
                    >
                        {error}
                    </div>
                ) : doctor ? (
                    <div className="space-y-2">
                        <div
                            className="flex items-center gap-2 text-xs"
                            style={{
                                color: doctor.healthy ? 'var(--theme-success)' : 'var(--theme-warning)',
                                fontFamily: 'var(--theme-font-mono)',
                            }}
                        >
                            {doctor.healthy ? (
                                <CheckCircle2 className="w-3.5 h-3.5" />
                            ) : (
                                <AlertTriangle className="w-3.5 h-3.5" />
                            )}
                            {doctor.summary}
                        </div>
                        {doctor.checks.map((check) => {
                            const vis = CHECK_STATUS_STYLE[check.status] ?? CHECK_STATUS_STYLE.warn;
                            return (
                                <div
                                    key={check.name}
                                    className="flex items-start gap-2 text-[10px]"
                                    style={{ fontFamily: 'var(--theme-font-mono)' }}
                                >
                                    <vis.Icon
                                        className="w-3 h-3 mt-0.5 flex-shrink-0"
                                        style={{ color: vis.color }}
                                    />
                                    <div>
                                        <span style={{ color: 'var(--theme-text-primary)' }}>
                                            {check.name}
                                        </span>
                                        <span
                                            className="ml-2"
                                            style={{ color: 'var(--theme-text-muted)' }}
                                        >
                                            {check.detail}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : null}
            </SettingsSection>

            {/* Scheduled Jobs */}
            <SettingsSection title="Scheduled Jobs" defaultExpanded={false}>
                <NotConnected reason="Scheduler endpoint exists in the engine but is not wired into the API router." />
            </SettingsSection>

            {/* Standing Orders */}
            <SettingsSection title="Standing Orders" defaultExpanded={false}>
                <NotConnected reason="Standing orders model exists but no REST endpoint is exposed." />
            </SettingsSection>
        </div>
    );
};

const NotConnected: React.FC<{ reason: string }> = ({ reason }) => (
    <div
        className="text-[10px] px-3 py-2 rounded"
        style={{
            color: 'var(--theme-text-muted)',
            background: 'color-mix(in srgb, var(--theme-surface-tertiary) 50%, transparent)',
            border: '1px solid var(--theme-surface-tertiary)',
            fontFamily: 'var(--theme-font-mono)',
            borderRadius: 'var(--theme-radius-sm)',
        }}
    >
        Not connected — {reason}
    </div>
);
